import { useState } from 'react';
import { championshipsApi } from '../../../lib/api';
import { toast } from 'sonner';

export const useChampionshipsActions = ({ isItalian }) => {
  const [championships, setChampionships] = useState([]);

  // Create
  const [showCreateChampionshipDialog, setShowCreateChampionshipDialog] = useState(false);
  const [championshipForm, setChampionshipForm] = useState({
    name: '', season: '2024/2025', max_teams: 20, entry_fee: 0, num_groups: 1, teams_per_group: 0, legs: 'solo_andata', tournament_type: 'groups',
    qualifiers_per_group: 2, extra_qualifiers_position: null, extra_qualifiers_count: 0,
    seed_method: 'random', knockout_start_round: 'auto',
    match_start_time: '21:00', tolerance_minutes: 15,
  });
  const [creatingChampionship, setCreatingChampionship] = useState(false);

  // Delete
  const [showDeleteChampionshipDialog, setShowDeleteChampionshipDialog] = useState(false);
  const [championshipToDelete, setChampionshipToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Expand clubs
  const [expandedChampClubs, setExpandedChampClubs] = useState(null);
  const [removingClub, setRemovingClub] = useState(null);

  const handleCreateChampionship = async () => {
    if (!championshipForm.name.trim()) { toast.error(isItalian ? 'Inserisci il nome del campionato' : 'Enter championship name'); return; }
    setCreatingChampionship(true);
    try {
      const response = await championshipsApi.create({
        name: championshipForm.name, season: championshipForm.season,
        max_teams: championshipForm.num_groups > 1
          ? (championshipForm.num_groups * championshipForm.teams_per_group)
          : (championshipForm.max_teams || 20),
        entry_fee: championshipForm.entry_fee || 0,
        num_groups: championshipForm.tournament_type === 'knockout' ? 1 : (championshipForm.num_groups || 1),
        teams_per_group: championshipForm.num_groups > 1 ? championshipForm.teams_per_group : null,
        legs: championshipForm.legs || 'solo_andata',
        tournament_type: championshipForm.tournament_type || 'groups',
        qualifiers_per_group: championshipForm.qualifiers_per_group || 2,
        extra_qualifiers_position: championshipForm.extra_qualifiers_position || null,
        extra_qualifiers_count: championshipForm.extra_qualifiers_position ? (championshipForm.extra_qualifiers_count || 0) : 0,
        seed_method: championshipForm.seed_method || 'random',
        knockout_start_round: championshipForm.knockout_start_round || 'auto',
        match_start_time: championshipForm.match_start_time || '21:00',
        tolerance_minutes: Number.isFinite(parseInt(championshipForm.tolerance_minutes))
          ? parseInt(championshipForm.tolerance_minutes)
          : 15,
      });
      setChampionships(prev => [...prev, response.data]);
      toast.success(isItalian ? 'Campionato creato!' : 'Championship created!');
      setShowCreateChampionshipDialog(false);
      setChampionshipForm({ name: '', season: '2024/2025', max_teams: 20, entry_fee: 0, num_groups: 1, teams_per_group: 0, legs: 'solo_andata', tournament_type: 'groups', qualifiers_per_group: 2, extra_qualifiers_position: null, extra_qualifiers_count: 0, seed_method: 'random', knockout_start_round: 'auto', match_start_time: '21:00', tolerance_minutes: 15 });
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore creazione' : 'Creation error'));
    } finally {
      setCreatingChampionship(false);
    }
  };

  const handleDeleteChampionship = (champ) => { setChampionshipToDelete(champ); setShowDeleteChampionshipDialog(true); };

  const confirmDeleteChampionship = async () => {
    if (!championshipToDelete) return;
    setDeleting(true);
    try {
      await championshipsApi.delete(championshipToDelete.championship_id);
      setChampionships(prev => prev.filter(c => c.championship_id !== championshipToDelete.championship_id));
      toast.success(isItalian ? `Campionato "${championshipToDelete.name}" eliminato!` : `Championship "${championshipToDelete.name}" deleted!`);
      setShowDeleteChampionshipDialog(false); setChampionshipToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore eliminazione' : 'Delete error'));
    } finally {
      setDeleting(false);
    }
  };

  const handleRemoveClubFromChampionship = async (championshipId, clubId, clubName) => {
    if (!window.confirm(isItalian
      ? `Sei sicuro di voler rimuovere "${clubName}" dal campionato? Le partite associate verranno eliminate.`
      : `Are you sure you want to remove "${clubName}" from the championship? Related matches will be deleted.`
    )) return;
    setRemovingClub(clubId);
    try {
      await championshipsApi.removeClub(championshipId, clubId);
      toast.success(isItalian ? `"${clubName}" rimosso dal campionato!` : `"${clubName}" removed from championship!`);
      const res = await championshipsApi.listAdmin();
      setChampionships(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore rimozione club' : 'Error removing club'));
    }
    setRemovingClub(null);
  };

  const handleToggleRegistration = async (champ) => {
    const newStatus = champ.status === 'registration' ? 'in_progress' : 'registration';
    try {
      await championshipsApi.updateStatus(champ.championship_id, newStatus);
      setChampionships(prev => prev.map(c =>
        c.championship_id === champ.championship_id ? { ...c, status: newStatus } : c
      ));
      toast.success(newStatus === 'in_progress'
        ? (isItalian ? 'Iscrizioni chiuse!' : 'Registrations closed!')
        : (isItalian ? 'Iscrizioni riaperte!' : 'Registrations reopened!'));
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  const handleCompleteChampionship = async (champ) => {
    try {
      await championshipsApi.updateStatus(champ.championship_id, 'completed');
      setChampionships(prev => prev.map(c =>
        c.championship_id === champ.championship_id ? { ...c, status: 'completed' } : c
      ));
      toast.success(isItalian ? 'Campionato completato!' : 'Championship completed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  const handleSuspendChampionship = async (champ) => {
    const newStatus = champ.status === 'suspended' ? 'in_progress' : 'suspended';
    try {
      await championshipsApi.updateStatus(champ.championship_id, newStatus);
      setChampionships(prev => prev.map(c =>
        c.championship_id === champ.championship_id ? { ...c, status: newStatus } : c
      ));
      toast.success(newStatus === 'suspended'
        ? (isItalian ? 'Campionato sospeso!' : 'Championship suspended!')
        : (isItalian ? 'Campionato riattivato!' : 'Championship resumed!'));
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  const handleGenerateSchedule = async (champ, legsOverride = null) => {
    if ((champ.clubs?.length || 0) < 2) {
      toast.error(isItalian ? 'Servono almeno 2 club iscritti' : 'At least 2 clubs required');
      return;
    }
    // Conferma se si tratta di rigenerazione (torneo non più in registration)
    if (champ.status !== 'registration') {
      const msg = isItalian
        ? `Rigenerare il calendario di "${champ.name}"? Tutte le partite NON completate verranno cancellate e ricreate${legsOverride ? ` come ${legsOverride === 'solo_andata' ? 'solo andata' : 'andata e ritorno'}` : ''}.`
        : `Regenerate schedule for "${champ.name}"? All non-completed matches will be deleted and recreated.`;
      if (!window.confirm(msg)) return;
    }
    try {
      const res = await championshipsApi.generateSchedule(champ.championship_id, legsOverride);
      toast.success(res.data?.message || (isItalian ? 'Calendario generato!' : 'Schedule generated!'));
      setChampionships(prev => prev.map(c =>
        c.championship_id === champ.championship_id
          ? { ...c, status: c.status === 'registration' ? 'in_progress' : c.status, legs: legsOverride || c.legs }
          : c
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore nella generazione del calendario' : 'Error generating schedule'));
    }
  };

  return {
    championships, setChampionships,
    setShowCreateChampionshipDialog,
    handleGenerateSchedule, handleToggleRegistration,
    handleSuspendChampionship, handleCompleteChampionship,
    handleDeleteChampionship, handleRemoveClubFromChampionship,
    expandedChampClubs, setExpandedChampClubs, removingClub,

    // Create dialog
    showCreateChampionshipDialog, championshipForm, setChampionshipForm,
    creatingChampionship, handleCreateChampionship,

    // Delete dialog
    showDeleteChampionshipDialog, setShowDeleteChampionshipDialog,
    championshipToDelete, setChampionshipToDelete, confirmDeleteChampionship, deleting,
  };
};
